import type {
  BackendShippingProviderName,
  CreateOrderItemDTO,
  CreateUpdateOrderDTO,
  DiscountDTO,
  Option,
  OrderDTO,
  PaymentProviderName
} from 'ecosystem'
import { OrderStatus } from 'ecosystem'
import { createEndpointError, fetchPropsFactory, storefrontApiCall } from '../../utils/fetching'
import { ORDERS_ENDPOINT } from './constants'

interface CreateStorefrontOrderParams {
  storeId: string
  backendOrderLines: CreateOrderItemDTO[]
  cartDiscountCode: Option<DiscountDTO>
  shipping?: {
    /** Never `DEFAULT` — the carrier-less branch omits `shipping` entirely so
     *  `Order.shippingProvider` stays unset. */
    provider: BackendShippingProviderName
    providerSessionId?: OrderDTO['shippingId']
  }
  paymentProviderInfo: {
    providerName: PaymentProviderName
  }
  /** The signed-in shopper, resolved from the iron-session by the route entry.
   *  Stamps `Order.customerId` at creation so the order belongs to the account
   *  rather than to whatever email is typed into the billing address later.
   *  Omit for anonymous shoppers — guest checkout then resolves by email at
   *  provisioning, unchanged. */
  customerId?: Option<string>
}

export async function createStorefrontOrder({
  storeId,
  backendOrderLines,
  cartDiscountCode,
  shipping,
  paymentProviderInfo,
  customerId
}: CreateStorefrontOrderParams): Promise<OrderDTO> {
  const payload: CreateUpdateOrderDTO = {
    status: OrderStatus.CREATED,
    storeId,
    items: backendOrderLines,
    // The identity travels as `customerId`; the block stays null so the BE never
    // has an email to resolve an identity from.
    customer: null,
    ...(customerId ? { customerId } : {}),
    paymentProvider: paymentProviderInfo.providerName,
    ...(shipping
      ? {
          shippingProvider: shipping.provider,
          shippingId: shipping.providerSessionId
        }
      : {}),
    ...(cartDiscountCode?.code ? { discountCode: cartDiscountCode?.code } : {})
  }

  const props = fetchPropsFactory({
    props: {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(payload)
    }
  })

  const { data: order, error } = await storefrontApiCall<OrderDTO>(ORDERS_ENDPOINT, props)
  if (error || !order) {
    return createEndpointError('create storefront order')
  }

  return order
}
